/**
 * 标准配合求解失败现场自动采集。
 *
 * 文件职责：
 * 1. 在预览求解、拖拽重求解、文档恢复求解等业务出口返回非 Solved 时，自动缓存**全精度**求解输入与诊断；
 * 2. 指纹去重 + 环形缓冲，保证被约束挡住的拖拽逐帧失败不会刷爆内存（同一失败只保留首帧现场并累计次数）；
 * 3. 采集记录的 `input` 就是可直接回放的 `StandardMateSolverInput` plain-data 克隆（仅剔除不可序列化的
 *    `debugTrace`），零转换即可落成离线 repro fixture——这是 Phase 0“失败现场 → fixture 流水线”的数据源；
 * 4. 只消费 plain-data，不依赖 Viewer、UI 或浏览器专有类型。
 *
 * 开关约定（沿用求解器追踪全局键 `__PUPPYCAD_STANDARD_MATE_SOLVER_TRACE__`）：
 * - 默认开启（纯内存、去重后成本可忽略；上报/落盘由上层按需消费采集缓存）；
 * - `{ autoCaptureUnsolved: false }` 显式关闭；
 * - `{ autoCaptureUnsolved: { maxCount, anonymizeComponentIds } }` 自定义缓存条数与组件 ID 脱敏。
 */

import type { MateSolveStatus } from '../definition';
import { STANDARD_MATE_SOLVER_DEBUG_TRACE_GLOBAL_KEY } from './StandardMateSolverDebugTrace';
import type {
  AssemblyMateSolveDiagnostic,
  StandardMateSolverInput,
} from './StandardMateSolverTypes';

/** 浏览器或宿主环境中缓存求解失败采集记录的全局键。 */
export const STANDARD_MATE_SOLVER_UNSOLVED_CAPTURES_GLOBAL_KEY = '__PUPPYCAD_STANDARD_MATE_UNSOLVED_CAPTURES__';

/** 采集缓存默认保留条数（按指纹去重后的独立失败现场数）。 */
const DEFAULT_UNSOLVED_CAPTURE_MAX_COUNT = 20;

/**
 * 求解失败采集配置。
 */
export interface StandardMateSolverUnsolvedCaptureOptions {
  /** 是否开启采集；缺省开启。 */
  readonly enabled?: boolean;
  /** 缓存最大条数；缺省 20。 */
  readonly maxCount?: number;
  /** 是否把组件实例 ID（含 stableRef 等字段内的编码变体）替换为匿名占位；缺省关闭。 */
  readonly anonymizeComponentIds?: boolean;
}

/**
 * 单条求解失败采集记录。
 */
export interface StandardMateSolverUnsolvedCaptureRecord {
  /** 记录结构版本。 */
  readonly schemaVersion: 1;
  /** 首次捕获时间（ISO 字符串）。 */
  readonly capturedAt: string;
  /** 最近一次同指纹失败时间（ISO 字符串）。 */
  readonly lastCapturedAt: string;
  /** 同指纹失败累计次数（拖拽逐帧失败会在此累加而不追加新记录）。 */
  readonly occurrenceCount: number;
  /** 业务出口标识（如 mate-preview / committed-transform / document-restore）。 */
  readonly context: string;
  /** 求解整体状态。 */
  readonly status: MateSolveStatus;
  /** 非 Solved 的 Mate ID 列表。 */
  readonly unsolvedMateIds: readonly string[];
  /** 求解诊断克隆。 */
  readonly diagnostics: readonly AssemblyMateSolveDiagnostic[];
  /**
   * 全精度求解输入克隆（剔除 `debugTrace`）。
   *
   * 可直接强转为 {@link StandardMateSolverInput} 离线回放，或经
   * `scripts/standard-mate-capture-to-fixture.mjs` 落成 repro fixture。
   */
  readonly input: unknown;
}

/**
 * 读取可写全局记录。
 *
 * @returns 全局记录对象。
 */
function readGlobalRecord(): Record<string, unknown> {
  return globalThis as Record<string, unknown>;
}

/**
 * 判断未知值是否为普通记录。
 *
 * @param value 未知值。
 * @returns 是否为记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/**
 * 从追踪全局键解析采集配置。
 *
 * 说明：追踪全局键取 boolean（仅控制追踪）或缺省时，采集按默认配置开启；只有显式
 * `autoCaptureUnsolved: false` 才关闭采集——追踪与采集是两个独立消费方。
 *
 * @returns 采集配置；关闭时返回 null。
 */
function resolveUnsolvedCaptureOptions(): Required<StandardMateSolverUnsolvedCaptureOptions> | null {
  const raw = readGlobalRecord()[STANDARD_MATE_SOLVER_DEBUG_TRACE_GLOBAL_KEY];
  const configured = isRecord(raw) ? raw.autoCaptureUnsolved : undefined;
  if (configured === false) {
    return null;
  }
  const options = isRecord(configured) ? configured as StandardMateSolverUnsolvedCaptureOptions : {};
  if (options.enabled === false) {
    return null;
  }
  return {
    enabled: true,
    maxCount: typeof options.maxCount === 'number' && options.maxCount > 0
      ? Math.floor(options.maxCount)
      : DEFAULT_UNSOLVED_CAPTURE_MAX_COUNT,
    anonymizeComponentIds: options.anonymizeComponentIds === true,
  };
}

/**
 * 读取全局采集缓存数组（可变引用）。
 *
 * @returns 采集缓存数组。
 */
function resolveCaptureStore(): StandardMateSolverUnsolvedCaptureRecord[] {
  const globalRecord = readGlobalRecord();
  const existing = globalRecord[STANDARD_MATE_SOLVER_UNSOLVED_CAPTURES_GLOBAL_KEY];
  if (Array.isArray(existing)) {
    return existing as StandardMateSolverUnsolvedCaptureRecord[];
  }
  const store: StandardMateSolverUnsolvedCaptureRecord[] = [];
  globalRecord[STANDARD_MATE_SOLVER_UNSOLVED_CAPTURES_GLOBAL_KEY] = store;
  return store;
}

/**
 * 生成失败指纹：同一业务出口下、同一组失败 Mate 的重复失败视为同一现场。
 *
 * @param input 指纹要素。
 * @returns 指纹字符串。
 */
function resolveCaptureFingerprint(input: {
  readonly context: string;
  readonly status: MateSolveStatus;
  readonly unsolvedMateIds: readonly string[];
  readonly transientMateIds: readonly string[];
}): string {
  return [
    input.context,
    input.status,
    [...input.unsolvedMateIds].sort().join(','),
    [...input.transientMateIds].sort().join(','),
  ].join('|');
}

/**
 * 剔除不可序列化字段后深克隆求解输入。
 *
 * @param solverInput 求解输入。
 * @returns 全精度 plain-data 克隆。
 */
function cloneSolverInputForCapture(solverInput: StandardMateSolverInput): unknown {
  const { debugTrace: _debugTrace, ...serializable } = solverInput;
  return JSON.parse(JSON.stringify(serializable));
}

/**
 * 把记录中的组件实例 ID 替换为匿名占位。
 *
 * 说明：组件 ID 也会以 URL 编码（一次/两次）形态嵌在 stableRef、topologyRef 等字段中，
 * 因此按“原文 + 编码变体”整体替换；同一记录内替换稳定一致，不破坏回放。
 *
 * @param record 采集记录。
 * @param componentInstanceIds 输入中出现的组件实例 ID。
 * @returns 脱敏后的采集记录。
 */
function anonymizeCaptureRecord(
  record: StandardMateSolverUnsolvedCaptureRecord,
  componentInstanceIds: readonly string[],
): StandardMateSolverUnsolvedCaptureRecord {
  let serialized = JSON.stringify(record);
  const sortedIds = [...componentInstanceIds].sort((left, right) => right.length - left.length);
  sortedIds.forEach((componentInstanceId, index) => {
    const placeholder = `component-${index}`;
    const variants = [
      componentInstanceId,
      encodeURIComponent(componentInstanceId),
      encodeURIComponent(encodeURIComponent(componentInstanceId)),
    ];
    variants.forEach((variant) => {
      // JSON 字符串内的原文以转义后形态出现，用 JSON.stringify 去掉包裹引号得到匹配片段。
      const escapedVariant = JSON.stringify(variant).slice(1, -1);
      const escapedPlaceholder = JSON.stringify(placeholder).slice(1, -1);
      serialized = serialized.split(escapedVariant).join(escapedPlaceholder);
    });
  });
  return JSON.parse(serialized) as StandardMateSolverUnsolvedCaptureRecord;
}

/**
 * 采集一次非 Solved 求解现场。
 *
 * @param input 采集输入；`context` 为业务出口标识。
 */
export function captureStandardMateSolverUnsolvedResult(input: {
  readonly context: string;
  readonly solverInput: StandardMateSolverInput;
  readonly status: MateSolveStatus;
  readonly diagnostics: readonly AssemblyMateSolveDiagnostic[];
}): void {
  const options = resolveUnsolvedCaptureOptions();
  if (!options) {
    return;
  }

  const unsolvedMateIds = [...new Set(
    input.diagnostics
      .filter((diagnostic) => diagnostic.status !== 'solved')
      .map((diagnostic) => diagnostic.mateId),
  )];
  const fingerprint = resolveCaptureFingerprint({
    context: input.context,
    status: input.status,
    unsolvedMateIds,
    transientMateIds: [...(input.solverInput.transientMateIds ?? [])],
  });

  const store = resolveCaptureStore();
  const now = new Date().toISOString();
  const existingIndex = store.findIndex((record) => (
    resolveCaptureFingerprint({
      context: record.context,
      status: record.status,
      unsolvedMateIds: record.unsolvedMateIds,
      transientMateIds: extractTransientMateIds(record.input),
    }) === fingerprint
  ));
  if (existingIndex >= 0) {
    const existing = store[existingIndex]!;
    store[existingIndex] = {
      ...existing,
      occurrenceCount: existing.occurrenceCount + 1,
      lastCapturedAt: now,
    };
    return;
  }

  let record: StandardMateSolverUnsolvedCaptureRecord = {
    schemaVersion: 1,
    capturedAt: now,
    lastCapturedAt: now,
    occurrenceCount: 1,
    context: input.context,
    status: input.status,
    unsolvedMateIds,
    diagnostics: input.diagnostics.map((diagnostic) => ({ ...diagnostic })),
    input: cloneSolverInputForCapture(input.solverInput),
  };
  if (options.anonymizeComponentIds) {
    record = anonymizeCaptureRecord(record, Object.keys(input.solverInput.components));
  }

  store.push(record);
  if (store.length > options.maxCount) {
    store.splice(0, store.length - options.maxCount);
  }
  // 与刚体不变量守卫同风格：新失败现场在控制台留一条可检索标记，便于开发期立刻注意到。
  // eslint-disable-next-line no-console
  console.warn('[PUPPYCAD_MATE_UNSOLVED_CAPTURE]', input.context, input.status, unsolvedMateIds.join(','));
}

/**
 * 从采集记录的输入克隆中提取瞬态 Mate ID（用于指纹重算）。
 *
 * @param capturedInput 记录内的输入克隆。
 * @returns 瞬态 Mate ID 列表。
 */
function extractTransientMateIds(capturedInput: unknown): readonly string[] {
  if (!isRecord(capturedInput) || !Array.isArray(capturedInput.transientMateIds)) {
    return [];
  }
  return capturedInput.transientMateIds.filter((value): value is string => typeof value === 'string');
}

/**
 * 读取当前采集缓存。
 *
 * @returns 采集记录列表（只读视图）。
 */
export function readStandardMateSolverUnsolvedCaptures(): readonly StandardMateSolverUnsolvedCaptureRecord[] {
  return [...resolveCaptureStore()];
}

/**
 * 清空采集缓存。
 */
export function clearStandardMateSolverUnsolvedCaptures(): void {
  resolveCaptureStore().length = 0;
}
